import {default as React, useState} from "react";
import {Modal, ModalBody, ModalHeader} from 'reactstrap';
import {CardObject} from "../../../types.d";
import {TopicModal} from "./TopicModal";
import {approvedText, escalationText} from "../../../utils/approvedText";
import {MiniPlayerComponent} from "../MiniPlayerComponent";
import {InlineFormattedString} from "../InlineFormattedString";

interface VocabularyModalProps {
    card: CardObject;
    visible: boolean;
    toggle: () => any;
}

export const VocabularyModal = (props: VocabularyModalProps) => {
    const [topicVisible, setTopicVisible] = useState<boolean>(false);

    const { card } = props;
    const { wordTranslation } = card;

    return <Modal isOpen={props.visible}
                  className={'large-modal'}
                  toggle={props.toggle}>
        <ModalHeader toggle={props.toggle}>Card "{wordTranslation.sourceWord.lemma} -> {wordTranslation.translationWord.lemma}"</ModalHeader>
        <ModalBody>
            <div className="modal-body">
                <div className="row">
                    <div className="col-4">
                        <p>
                            <strong>Vocabulary: </strong>
                            { wordTranslation.sourceWord.lemma }
                            <br/>
                            <strong>Translation: </strong>
                            { wordTranslation.translationWord.lemma }
                            <br/>
                            {/*<audio controls={true} src={ wordTranslation.audio }>It
                                seems like you browser can not play audio
                            </audio>*/}
                            <MiniPlayerComponent url={wordTranslation.audio} />
                        </p>
                        <p>
                            <strong>Course: </strong>
                            { card.course.sourceLanguage.code } -&gt; { card.course.translationLanguage.code }
                        </p>
                        <p>
                            <strong>Translation Creator: </strong> { card.creatorName }<br/>
                            <strong>Approving: </strong> { card.approvingName }
                            &nbsp;{ approvedText(card.approvedAt, card.disapprovedAt) }<br/>
                        </p>
                        { card.reviewDescription && <div style={{ backgroundColor: 'red', color: 'white' }}>
                            <strong>Comment: </strong>
                            <p style={{ whiteSpace: 'pre' }}>{ card.reviewDescription }</p>
                        </div> }
                        { card.escalatingName && (
                            <>
                                <strong>Escalating: </strong> { card.escalatingName }
                                &nbsp;{ escalationText(card) }
                            </>
                        ) }
                        <p></p>
                    </div>
                    <div className="col-4">
                        <div>
                            <OriginalText
                                title={'1st Enhancer'}
                                value={card.exampleSentence}
                                originalValue={card.exampleSentenceOriginal} />

                            <OriginalText
                                title={'1st Translation'}
                                value={card.exampleSentenceTranslation}
                                originalValue={card.exampleSentenceTranslationOriginal} />

                            <OriginalAudio
                                title={'1st Audio'}
                                value={card.sentenceAudio}
                                originalValue={card.sentenceAudioOriginal} />
                        </div>
                        <br/>
                        <p>
                            <OriginalText
                                title={'2nd Enhancer'}
                                value={card.exampleSentence2}
                                originalValue={card.exampleSentence2Original} />

                            <OriginalText
                                title={'2nd Translation'}
                                value={card.exampleSentenceTranslation2}
                                originalValue={card.exampleSentenceTranslation2Original} />

                            <OriginalAudio
                                title={'2nd Audio'}
                                value={card.sentenceAudio2}
                                originalValue={card.sentenceAudio2Original} />
                        </p>
                        <br/>
                        { card.topic && (
                            <p>
                                <strong>Topic: </strong>
                                <a href="" onClick={e => {
                                    e.preventDefault();

                                    setTopicVisible(true);
                                }}>{ card.topic.title }</a>

                                <TopicModal
                                    visible={topicVisible}
                                    toggle={() => setTopicVisible(!topicVisible)}
                                    card={card} />
                            </p>
                        ) }
                        <p></p>
                    </div>
                    <div className="col-4">
                        <p>
                            <strong>Info: </strong> <InlineFormattedString className={'content'} value={card.info} />
                        </p>
                        <p>
                            <strong>Derivable from words: </strong>
                            <InlineFormattedString className={'content'} value={card.derivableFromWords} />
                        </p>
                        { card.mnemo && (
                            <>
                                <p>
                                    <strong>Mnemo Keyword: </strong> {card.mnemo.mnemoKeyword}<br/>
                                    <strong>Short sentence: </strong>
                                </p>
                                <p dangerouslySetInnerHTML={{ __html: card.mnemo.shortSentence }} />
                                <br/>
                                <strong>Sentence: </strong> <span dangerouslySetInnerHTML={{ __html: card.mnemo.sentence }} />
                                <p></p>
                                { card.mnemo.disapproveComment && (
                                    <div style={{ backgroundColor: 'red', color: 'white' }}>
                                        <strong>Comment: </strong>
                                        { card.mnemo.disapproveComment }
                                    </div>
                                ) }
                                <div><strong>Mnemo Creator: </strong> { card.mnemo.postAuthorName }</div>
                                <div><strong>Approving: </strong> { card.mnemo.approvingName }
                                    &nbsp;{ approvedText(card.mnemo.approvedAt, card.mnemo.disapprovedAt) }</div>
                                <div><strong>Escalating: </strong> { card.mnemo.escalatingName }
                                    &nbsp;{ escalationText(card.mnemo) }</div>
                                <p></p>
                            </>
                        ) }
                    </div>
                </div>
            </div>
        </ModalBody>
    </Modal>
};

const OriginalAudio = (props: {
    title: string,
    value: string,
    originalValue: string,
}) => {
    const wasChanged = props.originalValue && props.originalValue !== props.value;

    return <>
        <strong>{ props.title } ORIGINAL: </strong>
        {/*<audio controls={true} src={ wasChanged ? props.originalValue : props.value }>It
            seems like you browser can not play audio
        </audio>*/}
        <MiniPlayerComponent url={ wasChanged ? props.originalValue : props.value } />
        { wasChanged && (
            <>
                <span style={{ backgroundColor: 'red', color: 'white' }}>
                    <strong>1st Audio CORRECTED: </strong>
                    {/*<audio controls={true} src={props.value}>It seems like you browser can not play audio</audio>*/}
                    <MiniPlayerComponent url={props.value} />
                </span>
                <br/>
            </>
        ) }
    </>
};

const OriginalText = (props: {
    title: string,
    value: string,
    originalValue: string,
}) => {
    const wasChanged = props.originalValue && props.originalValue !== props.value;

    return <>
        <strong>{ props.title } ORIGINAL: </strong>
        <span dangerouslySetInnerHTML={{ __html: wasChanged ? props.originalValue : props.value }} />
        <br/>
        { wasChanged && (
            <>
                <span style={{ backgroundColor: 'red', color: 'white' }}>
                    <strong>{ props.title } CORRECTED: </strong>
                    <span dangerouslySetInnerHTML={{ __html: props.value }} />
                </span>
                <br/>
            </>
        ) }
    </>
};
